import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, increment } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import MarkdownRenderer from './MarkdownRenderer';

const ArticleDetail = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const { currentUser, isAdminEmail } = useAuth();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const articleDoc = await getDoc(doc(db, 'articles', id));
        
        if (articleDoc.exists()) {
          setArticle({ id: articleDoc.id, ...articleDoc.data() });
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        setNotFound(true);
      }
      
      setLoading(false);
    };

    fetchArticle();
  }, [id]);

  const handleLike = async () => {
    if (!currentUser || !article) return;

    const isLiked = article.likedBy?.includes(currentUser.uid);
    const articleRef = doc(db, 'articles', article.id);
    
    try {
      const updatedLikedBy = isLiked
        ? article.likedBy.filter(uid => uid !== currentUser.uid)
        : [...(article.likedBy || []), currentUser.uid];

      await updateDoc(articleRef, {
        likedBy: updatedLikedBy,
        likesCount: increment(isLiked ? -1 : 1)
      });

      setArticle(prev => ({
        ...prev,
        likedBy: updatedLikedBy,
        likesCount: (prev.likesCount || 0) + (isLiked ? -1 : 1)
      }));
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser || !newComment.trim()) return;

    setSubmittingComment(true);
    
    try {
      const articleRef = doc(db, 'articles', article.id);
      const comment = {
        id: Date.now().toString(),
        text: newComment.trim(),
        author: currentUser.displayName || currentUser.email,
        authorId: currentUser.uid,
        authorEmail: currentUser.email,
        isAdmin: isAdminEmail(currentUser.email),
        createdAt: new Date()
      };

      await updateDoc(articleRef, {
        comments: arrayUnion(comment)
      });

      setArticle(prev => ({
        ...prev,
        comments: [...(prev.comments || []), comment]
      }));

      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
    
    setSubmittingComment(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-brown-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/blog" replace />;
  }

  const isLiked = currentUser && article.likedBy?.includes(currentUser.uid);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-brown-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link
          to="/blog"
          className="inline-flex items-center text-green-600 hover:text-green-800 mb-6"
        >
          ← Back to Blog
        </Link>

        {/* Article Content */}
        <article className="bg-white rounded-xl shadow-lg overflow-hidden">
          {article.imageUrl && (
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          )}

          <div className="p-8">
            {/* Article Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-2 flex-wrap">
                  {(article.categories && Array.isArray(article.categories) 
                    ? article.categories 
                    : [article.category]).filter(Boolean).map((category, index) => (
                    <span key={index} className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                      {category}
                    </span>
                  ))}
                </div>
                <span className="text-sm text-gray-500">
                  {article.createdAt?.toDate().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {article.title}
              </h1>

              <p className="text-xl text-gray-600 mb-6">
                {article.excerpt}
              </p>

              {/* Article Stats */}
              <div className="flex items-center gap-6 pb-6 border-b border-gray-200">
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 ${
                    isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                  }`}
                  disabled={!currentUser}
                >
                  <span className="text-xl">{isLiked ? '❤️' : '🤍'}</span>
                  <span>{article.likesCount || 0} likes</span>
                </button>

                <div className="flex items-center gap-2 text-gray-500">
                  <span className="text-xl">💬</span>
                  <span>{article.comments?.length || 0} comments</span>
                </div>
              </div>
            </div>

            {/* Article Body */}
            <MarkdownRenderer 
              content={article.content} 
              className="prose-lg max-w-none"
            />
          </div>
        </article>

        {/* Comments Section */}
        <div className="bg-white rounded-xl shadow-lg mt-8 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Comments ({article.comments?.length || 0})
          </h2>

          {/* Add Comment Form */}
          {currentUser ? (
            <form onSubmit={handleCommentSubmit} className="mb-8">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Share your thoughts..."
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                rows={4}
              />
              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={!newComment.trim() || submittingComment}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                >
                  {submittingComment ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            </form>
          ) : (
            <div className="mb-8 p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-gray-600">
                <Link to="/login" className="text-green-600 hover:text-green-800 font-medium">
                  Sign in
                </Link>{' '}
                to join the conversation
              </p>
            </div>
          )}

          {/* Comments List */}
          <div className="space-y-6">
            {article.comments?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No comments yet. Be the first to share your thoughts!
              </p>
            ) : (
              article.comments?.map((comment) => (
                <div key={comment.id} className={`border-b border-gray-200 pb-6 last:border-b-0 ${
                  comment.isAdmin || (comment.authorEmail && isAdminEmail(comment.authorEmail)) 
                    ? 'bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-500' 
                    : ''
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{comment.author}</span>
                      {(comment.isAdmin || (comment.authorEmail && isAdminEmail(comment.authorEmail))) && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Admin
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {comment.createdAt?.toDate?.()?.toLocaleDateString() || 'Just now'}
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{comment.text}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail;
